/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { omit } from 'lodash-es';

import {
  selectArnmRepairsState,
  filtersModalRulesArnmRepairsChange,
  resetModalArnmRepairsChange,
  filtersModalConditionArnmRepairsChange,
} from '@store';
import { getCleanedUrlParams, showError } from '@utils';
import { boardsServices } from '@services';
import { BOARDS_ID } from '@constants';
import { QUERY_KEYS } from 'constants/queryKeys';
import { useAssignees, useServicePoints } from 'hooks/common';
import {
  getArnmRepairsColumns,
  getArnmRepairsPayload,
  handleArnmRepairsData,
  normalizeOrderBy,
} from './utils';

export const ARM_REPAIRS_PAGE_SIZE = 500;
export const ARM_REPAIRS_EXPORT_LIMIT = 100000;

export const useArnmRepairsOperations = () => {
  const router = useRouter();
  const { filters } = useSelector(selectArnmRepairsState);
  const dispatch = useDispatch();
  const {
    assignees,
    assigneesWithNone,
    isFetching: isAssigneesFetching,
    isFetched: isAssigneesFetched,
  } = useAssignees();
  const { servicePoints, isFetching: isServicePointsFetching } =
    useServicePoints();

  const page = router.query?.page || 1;
  const pageSize = router.query?.pageSize || ARM_REPAIRS_PAGE_SIZE;
  const orderBy = router.query?.orderBy || 'createdAt';
  const order = router.query?.order || 'desc';
  const query = router.query?.query;

  const cleanedParams = getCleanedUrlParams({
    ...omit(router.query, ['view']),
    page,
    pageSize: parseInt(pageSize, 10) || ARM_REPAIRS_PAGE_SIZE,
    orderBy: normalizeOrderBy(orderBy),
    order,
    query,
  });

  const repairsResponse = useQuery(
    [
      QUERY_KEYS.GET_ARNM_REPAIRS_TASKS_KEY,
      { cleanedParams, filters: filters.modalFiltersRules },
    ],
    () => {
      const urlParams = getCleanedUrlParams({
        page,
        orderBy: normalizeOrderBy(orderBy),
        order,
        pageSize: parseInt(pageSize, 10) || ARM_REPAIRS_PAGE_SIZE,
        query,
      });
      const payload = getArnmRepairsPayload(
        cleanedParams,
        filters.modalFiltersRules
      );
      return boardsServices.getBoardTasks({
        boardId: BOARDS_ID.ARM_REPAIRS,
        params: urlParams,
        payload,
      });
    },
    { enabled: router.isReady && isAssigneesFetched }
  );

  const enumsResponse = useQuery(
    [QUERY_KEYS.GET_BOARDS_ENUMS, BOARDS_ID.ARM_REPAIRS],
    () => boardsServices.getEnumsPerBoardId(BOARDS_ID.ARM_REPAIRS),
    { enabled: router.isReady }
  );

  useEffect(() => {
    if (repairsResponse?.error) {
      showError(repairsResponse?.error);
    }
  }, [repairsResponse?.error]);

  const handleChangeRules = (rules) => {
    dispatch(filtersModalRulesArnmRepairsChange(rules));
  };

  const resetModalChange = useCallback(() => {
    dispatch(resetModalArnmRepairsChange());
  }, [dispatch]);

  const handleFiltersModalChange = useCallback(
    (conditions) => {
      dispatch(filtersModalConditionArnmRepairsChange(conditions));
    },
    [dispatch]
  );

  const arnmRepairs = handleArnmRepairsData(
    repairsResponse?.data?.data || [],
    assignees
  );
  const columns = getArnmRepairsColumns(arnmRepairs);

  const getRepairsExportPayload = () => {
    return {
      boardId: BOARDS_ID.ARM_REPAIRS,
      payload: {
        type: 'csv',
        rule: getArnmRepairsPayload(cleanedParams, filters.modalFiltersRules),
      },
      params: getCleanedUrlParams({
        orderBy: normalizeOrderBy(orderBy),
        order,
        query,
      }),
    };
  };

  return {
    page,
    total: repairsResponse?.data?.meta?.total,
    pageSize: parseInt(pageSize, 10) || ARM_REPAIRS_PAGE_SIZE,
    loading:
      repairsResponse?.isFetching ||
      isAssigneesFetching ||
      isServicePointsFetching,
    arnmRepairs,
    assignees,
    servicePoints,
    columns,
    filters,
    assigneesWithNone,
    enums: enumsResponse?.data?.data || {},
    getArnmRepairs: repairsResponse.refetch,
    handleFiltersModalChange,
    handleChangeRules,
    resetModalChange,
    getRepairsExportPayload,
  };
};
